import {createContext, type ReactNode, use, useCallback, useState} from 'react'

import {getFieldsForCapability} from '../../components/saved-view-options/get-fields-for-capability'
import {Capability} from '../../components/saved-view-options/fields'
import {useFields} from './FieldsProvider'

type SetVisibleField = (identifier: string, visible: boolean) => void

const VisibleFieldIdentifiersContext = createContext<ReadonlySet<string> | null>(null)
const SetVisibleFieldContext = createContext<SetVisibleField | null>(null)

/**
 * Standalone visibility state for repository saved views. Seeded once from all attributable
 * fields in FieldsProvider. Later metadata changes do not reset the selection; the host keys
 * it by view identity.
 *
 * Planning does not use this — it owns the draft in SavedViewConfigurationProvider and
 * plugs it into VisibleFieldsContextProvider directly.
 */
export function VisibleFieldsProvider({children}: {children: ReactNode}) {
  const view = useFields()
  const [identifiers, setIdentifiers] = useState<ReadonlySet<string>>(
    () => new Set(getFieldsForCapability(view, Capability.ATTRIBUTABLE).map(field => field.identifier)),
  )

  const setVisible = useCallback<SetVisibleField>((identifier, visible) => {
    setIdentifiers(current => {
      if (visible === current.has(identifier)) return current
      const next = new Set(current)
      if (visible) next.add(identifier)
      else next.delete(identifier)
      return next
    })
  }, [])

  return (
    <VisibleFieldsContextProvider identifiers={identifiers} setVisible={setVisible}>
      {children}
    </VisibleFieldsContextProvider>
  )
}

/**
 * Exposes an already-owned selection and setter to visibility hooks without owning another
 * draft. Planning owns its draft in SavedViewConfigurationProvider and plugs it in here.
 */
export function VisibleFieldsContextProvider({
  identifiers,
  setVisible,
  children,
}: {
  identifiers: ReadonlySet<string>
  setVisible: SetVisibleField
  children: ReactNode
}) {
  return (
    <SetVisibleFieldContext value={setVisible}>
      <VisibleFieldIdentifiersContext value={identifiers}>{children}</VisibleFieldIdentifiersContext>
    </SetVisibleFieldContext>
  )
}

export function useVisibleFieldIdentifiers() {
  const value = use(VisibleFieldIdentifiersContext)
  if (value === null) throw new Error('useVisibleFieldIdentifiers must be used within <VisibleFieldsProvider>')
  return value
}

export function useSetVisibleField() {
  const value = use(SetVisibleFieldContext)
  if (value === null) throw new Error('useSetVisibleField must be used within <VisibleFieldsProvider>')
  return value
}
